import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/leistungen/website-entwicklung")({
  head: () => {
    const h = pageHead("websiteDevelopment", "de");
    return { meta: h.meta, links: h.links };
  },
  component: WebsitePage,
});

const steps = [
  { num: "01", title: "Konzeption & Struktur", body: "Wir entwickeln Seitenarchitektur, Inhaltslogik und Nutzerführung auf Basis klarer Ziele und echter Nutzerbedürfnisse." },
  { num: "02", title: "Technische Umsetzung", body: "Die Umsetzung erfolgt sauber, performant und wartbar — mit Fokus auf Ladezeiten, Sicherheit und Erweiterbarkeit." },
  { num: "03", title: "Integration & Vorbereitung", body: "SEO-Grundlagen, Tracking, Performance-Marketing-Anforderungen und Schnittstellen werden von Beginn an berücksichtigt." },
  { num: "04", title: "Weiterentwicklung & Pflege", body: "Auch nach dem Go-live begleiten wir unsere Kunden bei Optimierung, Ausbau und technischer Pflege." },
];

const capabilities = [
  { name: "Seitenarchitektur", detail: "Inhaltslogik, Nutzerführung" },
  { name: "Performance-Optimierung", detail: "Ladezeiten, technische Basis" },
  { name: "SEO-Grundlagen", detail: "von Anfang an integriert" },
  { name: "Tracking-Integration", detail: "strukturell, nicht nachträglich" },
  { name: "Marketing-Verzahnung", detail: "Conversion-orientiert" },
  { name: "Mehrsprachigkeit", detail: "TYPO3, internationale Setups" },
  { name: "Headless mit Sanity", detail: "strukturierter Content-Layer" },
  { name: "Pflege & Weiterentwicklung", detail: "langfristig, partnerschaftlich" },
];

const stack = [
  { name: "WordPress", small: "flexibel, redaktionell" },
  { name: "TYPO3", small: "Enterprise, mehrsprachig" },
  { name: "Sanity", small: "Headless, kanalübergreifend" },
];

function WebsitePage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="Website-Entwicklung"
          headline={<>Eine Website ist <em>kein Plug-in-Friedhof.</em> Sie ist Performance-Asset.</>}
          lead="Wir bauen schnelle, SEO-stabile und gut wartbare Websites — auf Basis etablierter Systeme wie WordPress und TYPO3 oder headless mit Sanity. Keine Page-Builder-Geiseln, sondern Inhalte, die verstanden werden und gezielt führen."
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Leistungen", to: "/leistungen" }, { label: "Website-Entwicklung" }]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Websites als <em>funktionale Systeme</em> — nicht als digitale Visitenkarten.</>}>
          <RevealOnScroll>
            <div className="mb-10 rounded-lg border-l-4 border-lime bg-bg-subtle p-5 text-[15px] leading-relaxed text-ink-2">
              Sie brauchen einen Onlineshop? Das ist eine eigene Disziplin —{" "}
              <a href="/leistungen/onlineshop-entwicklung" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">
                zur Onlineshop-Entwicklung →
              </a>
            </div>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Viele Websites sehen gut aus. Aber nur wenige erfüllen ihren eigentlichen Zweck. Eine professionelle Website muss verstanden werden, Vertrauen aufbauen und Nutzer gezielt führen. Genau daran scheitern viele Projekte. Nicht an der Technik, sondern an fehlender Struktur und klarer Zielorientierung.</p>
              <p className="mb-5">In der Praxis begegnen uns immer wieder dieselben Probleme: Inhalte sind nicht auf Nutzerintention ausgerichtet, Seitenstrukturen sind historisch gewachsen, Ladezeiten und technische Basis bremsen Performance, Marketing und Tracking sind nicht verzahnt, Systeme lassen sich nur schwer erweitern oder pflegen. Das Ergebnis: Traffic ist da — Wirkung bleibt aus.</p>
              <p>Wir entwickeln Websites als funktionale Systeme. Am Anfang jeder Website-Entwicklung steht bei uns kein Design, sondern Verständnis: welche Ziele die Website erfüllen soll, welche Nutzer angesprochen werden, welche Inhalte wirklich relevant sind, welche Systeme und Schnittstellen notwendig sind. Erst danach beginnt die Umsetzung.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Vorgehen" headline={<>Vier Phasen vom <em>Verständnis</em> zur tragenden Website.</>}>
          <ProcessFlow steps={steps} />
        </Section>

        <Section number="04" kicker="Headless-Option" headline={<>Manche Projekte brauchen kein neues CMS. Sie brauchen <em>ein anderes Fundament.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Für die meisten Websites sind WordPress oder TYPO3 genau richtig — wenn man sie als Performance-Asset baut und nicht im Plug-in-Wildwuchs ersticken lässt. Es gibt aber Projekte, bei denen ein klassisches CMS an seine Grenzen stößt: mehrsprachige Auftritte, Inhalte, die gleichzeitig auf Website, App und Newsletter laufen müssen, oder Marken, die maximale Geschwindigkeit und volle gestalterische Freiheit verlangen. Für genau diese Fälle setzen wir auf einen Headless-Ansatz mit <strong>Sanity</strong>.</p>
              <p className="mb-8"><strong>Was Sanity anders macht:</strong> Eine zentrale Content-Quelle speist Website, App und Newsletter. Einmal gepflegt — überall ausgespielt.</p>
            </div>

            <div className="mt-10 grid gap-8 md:grid-cols-2">
              <div className="rounded-lg border-2 border-lime bg-white p-7">
                <h3 className="mb-4 font-display text-xl text-ink">Headless mit Sanity passt, wenn …</h3>
                <ul className="space-y-3 text-[15px] leading-relaxed text-ink-2">
                  <li>die Website mehrsprachig ist oder international ausgespielt wird</li>
                  <li>Inhalte parallel auf Web, App und Newsletter laufen</li>
                  <li>ein Team strukturiert und gemeinsam pflegt</li>
                  <li>Tempo, SEO und Erweiterbarkeit zentral sind</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-7">
                <h3 className="mb-4 font-display text-xl text-ink">Klassisches CMS ist besser, wenn …</h3>
                <ul className="space-y-3 text-[15px] leading-relaxed text-ink-2">
                  <li>es um eine überschaubare Unternehmens- oder Imageseite geht</li>
                  <li>das Team gewohnte WordPress-/TYPO3-Workflows behalten will</li>
                  <li>der Mehraufwand eines Headless-Aufbaus im Verhältnis zu groß wäre</li>
                </ul>
              </div>
            </div>

            <div className="mt-10 prose max-w-2xl text-[15px] leading-relaxed text-ink-3">
              <p className="mb-5"><strong className="text-ink">Was das konkret bringt:</strong> sehr schnelle, statisch gebaute Seiten · saubere Mehrsprachigkeit ohne Plug-in-Chaos · einmal pflegen, überall nutzen · maßgeschneiderte Redaktion · wartungsarm und sicher, da der Content-Layer von Sanity gehostet wird.</p>
              <p><strong className="text-ink">Was es kostet:</strong> Der Einstieg ist günstig bis kostenlos. Sanity arbeitet mit drei Stufen — ein großzügiger <strong>Free</strong>-Tarif (bis 20 Nutzer, ideal zum Start), der <strong>Growth</strong>-Tarif ab 15 $ pro aktiv arbeitendem Sitz und Monat sowie <strong>Enterprise</strong> auf Anfrage. Die genaue Kalkulation machen wir vorab transparent.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="05" kicker="Capabilities & Stack" headline={<>Womit wir Websites <em>tragfähig</em> machen.</>}>
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="de" number="06" />

        <RelatedServices
          locale="de"
          fromId="website-entwicklung"
          number="07"
          items={relatedBySource["website-entwicklung"]}
        />

        <Section number="08" kicker="Engagement">
          <CTABlock
            headline={<>Eine Website, die <em>trägt</em> — nicht nur präsentiert</>}
            body="Ob klassisches CMS oder Headless mit Sanity — wir klären zuerst, was wirtschaftlich Sinn ergibt. Wenn Sie eine Website möchten, die verständlich, leistungsfähig und zukunftssicher ist, sprechen wir gerne darüber."
            primary={{ label: "Kontakt aufnehmen", to: "/kontakt" }}
            secondary={{ label: "Alle Leistungen", to: "/leistungen" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
